import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import Modal from "../../components/common/modal";
import UploadImages from "../../components/upoloadimages";
import Button from "../../components/common/button";
import UploadDocument from "../../components/upoloadFiles";
import Toggle from "../../components/common/toggle";
import { categoryValidationSchema } from "../../validation/categoryValidation";
import { resolveImageUrl } from "../../utils/resolveImageUrl";

const createEmptyCatalog = () => ({
  id: `new-${Date.now()}-${Math.random()}`,
  title: "",
  description: "",
  existingFile: "",
  file: null,
});

const toBool = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;

  if (typeof value === "string") {
    const s = value.trim().toLowerCase();

    if (["true", "1", "active", "enabled", "yes"].includes(s)) return true;

    if (["false", "0", "inactive", "disabled", "no"].includes(s)) return false;
  }

  return false;
};

const CategoryModal = ({
  isOpen,
  onClose,
  editData,
  viewMode = false,
  mode = "create",
  onSubmit,
  isLoading = false,
}) => {
  const isEditing = mode === "edit";
  const isDelete = mode === "delete";

  const [catalogs, setCatalogs] = useState([]);
  const [deletedCatlogIds, setDeletedCatlogIds] = useState([]);

  // ─────────────────────────────────────────────
  // FORMIIK
  // ─────────────────────────────────────────────

  const formik = useFormik({
    enableReinitialize: true,

    initialValues: {
      title: editData?.title || "",
      slug: editData?.slug || "",
      description: editData?.description || "",

      isActive: toBool(
        typeof editData?.isActive !== "undefined"
          ? editData?.isActive
          : editData?.status,
      ),

      image: null,
    },

    validationSchema: categoryValidationSchema,

    onSubmit: async (values) => {
      await onSubmit({
        title: values.title,
        slug: values.slug,
        description: values.description,
        isActive: values.isActive,
        image: values.image,
        catlogs: catalogs,
        deletedCatlogIds,
      });
    },
  });

  // ─────────────────────────────────────────────
  // LOAD EXISTING CATALOGS
  // ─────────────────────────────────────────────

  useEffect(() => {
    if (!isOpen) return;

    if (!editData) {
      setCatalogs([createEmptyCatalog()]);
      setDeletedCatlogIds([]);
      return;
    }

    const rawCatlogs = editData.catlogs || editData.catelogs || [];

    setCatalogs(
      rawCatlogs.length
        ? rawCatlogs.map((cat, i) => ({
            id: cat._id || `existing-${i}`,
            _id: cat._id,
            title: cat.title || "",
            description: cat.description || "",
            existingFile: cat.file?.url || cat.file || "",
            file: null,
          }))
        : [createEmptyCatalog()],
    );
    setDeletedCatlogIds([]);
  }, [isOpen, editData]);

  // ─────────────────────────────────────────────
  // CATALOG HANDLERS
  // ─────────────────────────────────────────────

  const addCatalog = () => {
    setCatalogs((prev) => [...prev, createEmptyCatalog()]);
  };

  const getCatalogFileUrl = (filePath) => {
    if (!filePath || typeof filePath !== "string") return null;
    if (filePath.startsWith("http")) return filePath;
    return resolveImageUrl(filePath);
  };

  const updateCatalog = (id, field, value) => {
    setCatalogs((prev) =>
      prev.map((cat) => (cat.id === id ? { ...cat, [field]: value } : cat)),
    );
  };

  const removeCatalog = (id) => {
    const target = catalogs.find((c) => c.id === id);

    if (target?._id) {
      setDeletedCatlogIds((prev) => [...prev, target._id]);
    }

    setCatalogs((prev) => prev.filter((cat) => cat.id !== id));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        isDelete
          ? "Delete Category"
          : viewMode
            ? "View Category"
            : isEditing
              ? "Edit Category"
              : "Add Category"
      }
    >
      {isDelete ? (
        <>
          <div style={{ padding: "10px 2px" }}>
            <p style={{ margin: 0, color: "#333" }}>
              Are you sure you want to delete{" "}
              <strong>{editData?.title || "this category"}</strong>?
            </p>

            <p
              style={{
                margin: "8px 0 0",
                color: "#666",
                fontSize: 13,
              }}
            >
              This action will remove it from the database and the table.
            </p>
          </div>

          <div className="modal-footer">
            <Button onClick={onClose}>Cancel</Button>

            <Button onClick={() => onSubmit?.()} disabled={isLoading}>
              {isLoading ? "Deleting..." : "Delete"}
            </Button>
          </div>
        </>
      ) : (
        <form onSubmit={formik.handleSubmit} className="modal-form">
          {/* TITLE */}
          <div className="form-group">
            <label>Category Title</label>

            <input
              type="text"
              name="title"
              placeholder="Category Name"
              value={formik.values.title}
              onChange={(e) => {
                formik.handleChange(e);

                // auto slug
                formik.setFieldValue(
                  "title",
                  e.target.value
                    .toLowerCase()
                    .replace(/\s+/g, "-")
                    .replace(/[^a-z0-9-]/g, ""),
                );
              }}
              onBlur={formik.handleBlur}
              disabled={viewMode}
            />

            {formik.touched.title && formik.errors.title && (
              <p className="error-text">{formik.errors.title}</p>
            )}
          </div>

          {/* SLUG */}
          <div className="form-group">
            <label>Slug</label>

            <input
              type="text"
              name="slug"
              placeholder="Category Slug"
              value={formik.values.slug}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={viewMode}
            />

            {formik.touched.slug && formik.errors.slug && (
              <p className="error-text">{formik.errors.slug}</p>
            )}
          </div>

          {/* DESCRIPTION */}
          <div className="form-group">
            <label>Description</label>

            <textarea
              name="description"
              placeholder="Category Description"
              value={formik.values.description}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={viewMode}
            />

            {formik.touched.description && formik.errors.description && (
              <p className="error-text">{formik.errors.description}</p>
            )}
          </div>

          {/* STATUS */}
          <div
            className="form-group"
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              marginTop: 30,
            }}
          >
            <label>Status</label>

            <div className="sharing-toggle">
              <Toggle
                onChange={(checked) =>
                  formik.setFieldValue("isActive", checked)
                }
                checked={!!formik.values.isActive}
                disabled={viewMode}
              />
            </div>
          </div>

          {/* IMAGE */}
          <div
            className="form-group"
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 10,
              marginTop: 20,
            }}
          >
            <label>Category Image</label>

            {viewMode ? (
              (() => {
                const imgSrc = editData?.image?.url || editData?.image;

                if (!imgSrc) return <div>No Image</div>;

                const fullSrc = resolveImageUrl(imgSrc);

                return (
                  <img
                    src={fullSrc}
                    alt="category"
                    style={{
                      width: 120,
                      height: 120,
                      objectFit: "cover",
                      borderRadius: 10,
                    }}
                  />
                );
              })()
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                }}
              >
                {isEditing &&
                  (editData?.image?.url || editData?.image) &&
                  !formik.values.image && (
                    <div>
                      {(() => {
                        const imgSrc = editData?.image?.url || editData?.image;

                        const fullSrc = resolveImageUrl(imgSrc);

                        return (
                          <img
                            src={fullSrc}
                            alt="Current"
                            style={{
                              width: 80,
                              height: 80,
                              objectFit: "cover",
                              borderRadius: 8,
                              border: "1px solid #ddd",
                            }}
                          />
                        );
                      })()}
                    </div>
                  )}

                <UploadImages
                  showTitle={false}
                  onChange={(file) => formik.setFieldValue("image", file)}
                />

                {formik.touched.image && formik.errors.image && (
                  <p className="error-text">{formik.errors.image}</p>
                )}
              </div>
            )}
          </div>

          {/* CATALOGS */}
          <div className="form-section" style={{ marginTop: 20 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 12,
              }}
            >
              <h3 style={{ margin: 0, border: "none", padding: 0 }}>
                Catalog Files{" "}
                <span style={{ color: "#888", fontSize: 13, fontWeight: 500 }}>
                  ({catalogs.length})
                </span>
              </h3>

              {!viewMode && (
                <Button type="button" onClick={addCatalog}>
                  + Add Catalog
                </Button>
              )}
            </div>

            {catalogs.map((cat, index) => {
              const fileUrl = getCatalogFileUrl(cat.existingFile);

              return (
                <div
                  key={cat.id}
                  style={{
                    border: "1px solid #e5e7eb",
                    padding: 14,
                    borderRadius: 10,
                    marginBottom: 12,
                    background: "#fff",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 10,
                    }}
                  >
                    <strong style={{ fontSize: 13, color: "#555" }}>
                      Catalog #{index + 1}
                    </strong>

                    {!viewMode && catalogs.length > 1 && (
                      <Button
                        type="button"
                        onClick={() => removeCatalog(cat.id)}
                      >
                        Remove
                      </Button>
                    )}
                  </div>

                  <div className="form-group" style={{ marginTop: 0 }}>
                    <label>Catalog Title</label>
                    <input
                      placeholder="Catalog Title"
                      value={cat.title}
                      onChange={(e) =>
                        updateCatalog(cat.id, "title", e.target.value)
                      }
                      disabled={viewMode}
                    />
                  </div>

                  <div className="form-group">
                    <label>Catalog Description</label>
                    <textarea
                      placeholder="Catalog Description"
                      value={cat.description}
                      onChange={(e) =>
                        updateCatalog(cat.id, "description", e.target.value)
                      }
                      disabled={viewMode}
                    />
                  </div>

                  <div className="form-group">
                    <label>Catalog File</label>
                    {viewMode ? (
                      fileUrl ? (
                        <a
                          href={fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: "#5C308D", fontSize: 14 }}
                        >
                          {cat.existingFile.split("/").pop()}
                        </a>
                      ) : (
                        <span style={{ color: "#888", fontSize: 13 }}>
                          No file uploaded
                        </span>
                      )
                    ) : (
                      <UploadDocument
                        label="Upload Catalog File (PDF / CSV)"
                        onChange={(file) => updateCatalog(cat.id, "file", file)}
                        existingFile={cat.existingFile}
                        selectedFile={cat.file}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* FOOTER */}
          <div className="modal-footer">
            <Button type="button" className="CancelButton" onClick={onClose}>
              {viewMode ? "Close" : "Cancel"}
            </Button>

            {!viewMode && (
              <Button
                type="submit"
                className="CommonButton"
                disabled={isLoading}
              >
                {isEditing ? "Save Changes" : "Add Category"}
              </Button>
            )}
          </div>
        </form>
      )}
    </Modal>
  );
};

export default CategoryModal;
